import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import cn from 'clsx';
import { MonitoringToolkitContext } from './context';
import { IMonitoringToolkit, ITabs } from '@components-clean/core/monitoring-toolkit/interface';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useEventBus } from '@utils-clean/eventBus';
import LocalStorage from '@utils-clean/local-storage-service';
import './wrapper.scss';
import { IUser } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import { LoggedInPageProps } from '@config/routes/interfaces';

interface IToolkitOpenState {
    topPanelHeight: string;
    bottomPanelHeight: string;
    selectedTabIndex: number;
}

interface ToolKitHelper {
    toolkitHelper: IMonitoringToolkit;
}

export type MonitoringToolkitWrapperProps = LoggedInPageProps & PropsWithChildren & ToolKitHelper;

export interface IToggleToolkitEventBusMessage {
    topic: string;
    message: ToolkitStateValues;
}

export const TOGGLE_TOOLKIT = 'toggle';

export enum ToolkitStateValues {
    Collapsed = 1,
    HalfSize = 2,
    FullSize = 3,
}

const MonitoringToolkitWrapper = ({
    user,
    organisationKey,
    study,
    cm2Dataset: dataset,
    children,
    toolkitHelper,
}: MonitoringToolkitWrapperProps) => {
    const toolkitAnimationSpeedState = useState<number>(500);
    const { t } = useTranslation(toolkitHelper.translationKey, {
        keyPrefix: 'monitoringToolkit',
    });

    const unsavedChangesContext = useUnsavedChangesContext();
    const studyRef = study?.id as string;
    const studyNumber = study?.studyNumber as string;

    const canEdit = toolkitHelper.canEditToolkit(user as IUser);
    const stateRaw = LocalStorage.getItem(user!, toolkitHelper.TOOLKIT_STATE_KEY);
    const state = stateRaw ? (JSON.parse(stateRaw) as IToolkitOpenState) : null;

    // The defaults for these height states and tab selection will be determined once the page loads (from state or assumed defaults).
    const [topPanelHeight, setTopPanelHeight] = useState<string>(state?.topPanelHeight ?? toolkitHelper.AUTO_SIZE);
    const [bottomPanelHeight, setBottomPanelHeight] = useState<string>(
        state?.bottomPanelHeight ?? toolkitHelper.COLLAPSED_SIZE,
    );
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(state?.selectedTabIndex ?? 0);
    const [isAnimationEnabled, setIsAnimationEnabled] = useState<boolean>(
        !(state?.topPanelHeight.endsWith('px') ?? true),
    );

    // The page div reference is needed to determine how tall the page is when compared with how large the vertical scroll is. This is used
    // for snapping the toolkit shut.
    const pageDiv = useRef<HTMLDivElement>(null);
    // The top panel reference is used for working out the pixel offset when the user manually drags the toolkit size.
    const topPanel = useRef<HTMLDivElement>(null);
    // The bottom panel reference is used to work out if the panel is small enough to be automatically snapped shut.
    const bottomPanel = useRef<HTMLDivElement>(null);

    const bottomPanelRef = useMemo(() => ({ bottomPanelRef: bottomPanel, studyNumber }), [bottomPanel, studyRef]);

    const saveOpenState = () => {
        if (!user) return;
        LocalStorage.setItem(
            user,
            toolkitHelper.TOOLKIT_STATE_KEY,
            JSON.stringify({ topPanelHeight, bottomPanelHeight, selectedTabIndex }),
        );
    };

    useEffect(() => {
        if (!topPanelHeight || !bottomPanelHeight) return;
        saveOpenState();
    }, [topPanelHeight, bottomPanelHeight, selectedTabIndex]);

    const baseOnClick = useCallback(() => {
        // Turn back on animations if we're dealing with a non-pixel sized panel, as these will animate OK.
        if (!topPanelHeight.endsWith('px')) {
            setIsAnimationEnabled(true);
        }

        // We want to select a tab when we open the panel so that we don't have empty UI. Perhaps this is unnecessary.
        if (selectedTabIndex === undefined) {
            setSelectedTabIndex(0);
        }
    }, [topPanelHeight, setIsAnimationEnabled, selectedTabIndex, setSelectedTabIndex]);

    const onClickMinimise = () => {
        baseOnClick();

        setTopPanelHeight(toolkitHelper.AUTO_SIZE);
        setBottomPanelHeight(toolkitHelper.COLLAPSED_SIZE);
    };

    const onClickHalfScreen = useCallback(() => {
        baseOnClick();

        setTopPanelHeight(toolkitHelper.AUTO_SIZE);
        setBottomPanelHeight(toolkitHelper.AUTO_SIZE);
    }, [baseOnClick, toolkitHelper.AUTO_SIZE, setTopPanelHeight, setBottomPanelHeight]);

    const onClickFullscreen = () => {
        baseOnClick();

        setTopPanelHeight(toolkitHelper.COLLAPSED_SIZE);
        setBottomPanelHeight(toolkitHelper.AUTO_SIZE);
    };

    const isDragging = useRef<boolean>(false);

    const handleMouseMove = (e: globalThis.MouseEvent) => {
        if (!isDragging.current) return;

        setBottomPanelHeight(toolkitHelper.AUTO_SIZE);
        // This was originally using `topPanel.current?.offsetTop` but someone changed some display css and now it doesn't work. Using a hard
        // coded value works fine (as long as the top panel doesn't get any bigger).
        setTopPanelHeight(`${e.clientY - 97}px`);
    };

    const handleMouseUp = () => {
        isDragging.current = false;

        // We snap the toolbar shut (to exactly 0) if the manual resize was either:
        //    a) dropped off the page entirely
        //    b) dropped within a certain "too small to function" range
        const shouldSnapToShut =
            (pageDiv.current?.scrollHeight ?? 0) > (pageDiv.current?.clientHeight ?? 0) ||
            (bottomPanel.current?.clientHeight ?? 0) <= toolkitHelper.BOTTOM_PANEL_MIN_SIZE;

        if (!shouldSnapToShut) return;

        setBottomPanelHeight(toolkitHelper.COLLAPSED_SIZE);
        setTopPanelHeight(toolkitHelper.AUTO_SIZE);
    };

    // If the user navigates to a new page, we want to half screen the toolkit.
    useEffect(() => {
        if (topPanelHeight === toolkitHelper.COLLAPSED_SIZE) {
            onClickHalfScreen();
        }
    }, [location.pathname]);

    useEffect(() => {
        // Setup some of the manual dragging handlers
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const resizeToHalfScreenIfRequired = useCallback(() => {
        if (topPanelHeight !== toolkitHelper.COLLAPSED_SIZE && !topPanelHeight.endsWith('px')) {
            onClickHalfScreen();
        }
    }, [onClickHalfScreen, toolkitHelper.COLLAPSED_SIZE, topPanelHeight]);

    const setSelectedTab = useCallback(
        async (tabIndex: number, preventResize?: boolean) => {
            if (preventResize !== true) {
                resizeToHalfScreenIfRequired();
            }
            // Determine whether this specific type of toolkit has unsaved changes
            if (toolkitHelper.hasUnsavedChanges(unsavedChangesContext)) {
                const confirmed = await unsavedChangesContext.confirmDiscardChanges('toolkit-unsaved-dialog');
                if (!confirmed) {
                    return;
                }
                setSelectedTabIndex(tabIndex);
                // Clear unsaved changes for this specific type of toolkit
                toolkitHelper.clearUnsavedChanges(unsavedChangesContext);
            }
            setSelectedTabIndex(tabIndex);
        },
        [resizeToHalfScreenIfRequired, toolkitHelper, unsavedChangesContext, setSelectedTabIndex],
    );

    const handleTabSelection = async (e: TabStripSelectEventArguments) => {
        await setSelectedTab(e.selected);
    };

    interface ITabStrip {
        tabStripRef: { current: HTMLDivElement };
    }

    const tabstrip = useRef<ITabStrip>(null!);

    useEffect(() => {
        // We can't add attributes to the tab strip, so we need to add using this weird approach.
        const tabstripElement = tabstrip.current.tabStripRef.current;
        // ... adding in the test ID.
        tabstripElement.setAttribute('data-testid', 'toolkit-tabstrip');
        // ... adding in a handler to potentially open the toolkit if it's not already open.
        tabstripElement.onclick = resizeToHalfScreenIfRequired;
    });

    useEventBus<IToggleToolkitEventBusMessage>(TOGGLE_TOOLKIT, (toggleState: ToolkitStateValues) => {
        // eslint-disable-next-line default-case
        switch (toggleState) {
            case ToolkitStateValues.Collapsed:
                onClickMinimise();
                break;
            case ToolkitStateValues.HalfSize:
                onClickHalfScreen();
                break;
            case ToolkitStateValues.FullSize:
                onClickFullscreen();
                break;
        }
    });

    const isHalfScreen = bottomPanelHeight === toolkitHelper.AUTO_SIZE && topPanelHeight === toolkitHelper.AUTO_SIZE;

    const tabs: ITabs[] = useMemo(
        () =>
            !organisationKey || !studyRef
                ? []
                : toolkitHelper.tabsContent(
                      organisationKey,
                      studyRef,
                      studyNumber,
                      canEdit,
                      isHalfScreen,
                      dataset?.id as number,
                      setSelectedTab,
                      t,
                      study,
                  ),
        [
            toolkitHelper,
            organisationKey,
            studyRef,
            studyNumber,
            canEdit,
            isHalfScreen,
            dataset?.id,
            setSelectedTab,
            t,
            study,
        ],
    );

    function getSelectedTabContent() {
        const selectedTab = tabs.at(selectedTabIndex);

        return <div className={cn('panel-content', selectedTab?.tabContentClassName)}>{selectedTab?.content}</div>;
    }

    return (
        <MonitoringToolkitContext.Provider value={bottomPanelRef}>
            <div
                className="monitoring-toolkit-wrapper full-content-height-with-scroll k-pos-relative"
                style={{
                    gridTemplateRows: `${topPanelHeight ?? toolkitHelper.AUTO_SIZE} min-content min-content ${
                        bottomPanelHeight ?? toolkitHelper.COLLAPSED_SIZE
                    }`,
                    transition: isAnimationEnabled ? `all ${toolkitAnimationSpeedState[0]}ms` : undefined,
                }}
                ref={pageDiv}
            >
                <div className="panel main-panel" ref={topPanel}>
                    <div className="panel-content">{children}</div>
                </div>
                <div className="splitter-bar" role="presentation" />
                <div className="toolbar">
                    <div className="content">
                        <TabStrip
                            className="toolkit-tabstrip"
                            selected={selectedTabIndex ?? 0}
                            onSelect={handleTabSelection}
                            ref={tabstrip}
                        >
                            {tabs.map((tab) => (
                                <TabStripTab
                                    title={
                                        <div className={cn('tab-header-content', tab.tabHeaderClassName)}>
                                            <span className="long-title">{tab.title}</span>
                                            <span className="short-title">{tab.shortTitle}</span>
                                        </div>
                                    }
                                    key={tab.title}
                                />
                            ))}
                        </TabStrip>
                        <div className="buttons">
                            <button
                                data-testid="toolkit-minimise"
                                onClick={onClickMinimise}
                                disabled={bottomPanelHeight === toolkitHelper.COLLAPSED_SIZE}
                                aria-label="Minimise toolkit"
                                type="button"
                            >
                                <FontAwesomeIcon icon={['fas', 'window-minimize']} />
                            </button>
                            <button
                                data-testid="toolkit-half-screen"
                                onClick={onClickHalfScreen}
                                disabled={
                                    bottomPanelHeight === toolkitHelper.AUTO_SIZE &&
                                    topPanelHeight === toolkitHelper.AUTO_SIZE
                                }
                                aria-label="Half-screen toolkit"
                                type="button"
                            >
                                <FontAwesomeIcon icon={['far', 'window-maximize']} className="flip-v" />
                            </button>
                            <button
                                data-testid="toolkit-maximise"
                                onClick={onClickFullscreen}
                                disabled={topPanelHeight === toolkitHelper.COLLAPSED_SIZE}
                                aria-label="Maximise Toolkit"
                                type="button"
                            >
                                <FontAwesomeIcon icon={['fas', 'square']} style={{ fontSize: '17px' }} />
                            </button>
                        </div>
                    </div>
                    <div className="border-bottom" />
                </div>
                <div className="panel toolkit-panel k-pos-relative" ref={bottomPanel}>
                    {getSelectedTabContent()}
                </div>
            </div>
        </MonitoringToolkitContext.Provider>
    );
};

export default MonitoringToolkitWrapper;
